import { Injectable } from '@angular/core';
import {
    ConfigurationService,
    SiteSettingsService,
    SubscriptionCollector,
    TrackingV2Service,
    TranslationService
} from '@draeger-academy/common';
import { AppInfoService } from '@draeger-academy/common/lib/services/app-info/app-info.service';

import { StorageMap } from '@ngx-pwa/local-storage';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ECookieConsentStringMappingEnum } from './shared/enums/cookie-consent.enums';
import { ICookieConsent, TrackCookieSettingsInterface } from './shared/interfaces/cookie-consent.interface';

@Injectable()
export class CookieConsentOverlayService {
    @SubscriptionCollector() private _subscription;

    readonly _site: string;
    readonly _initialCookieConsentData: ICookieConsent = {
        Version: null,
        TechnicalRequired: 1,
        Functional: 0,
        Statistics: 0,
        Marketing: 0
    };
    public cookieConsentTranslations: any;
    public cookieReplacmentTranslations: any;
    public cookieConsentActive = false;
    public showCookieConsentOverlay$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public showCookieConsentOverlayFromFooter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public getCookiesToCheck: Array<string>;
    private _window: any;
    public gtmContainerId: string;
    private _pageRendered = false;
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});
    public site: string;
    public portal: string;
    siteCookieConsentExist: boolean;
    public currentPortal: string;

    private _$portalId: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalId$(): Observable<string> {
        return this._$portalId.asObservable();
    }
    private _$portalSiteSettings: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalSiteSettings$(): Observable<string> {
        return this._$portalSiteSettings.asObservable();
    }
    private _$cookieConsentActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get cookieConsentActive$(): Observable<boolean> {
        return this._$cookieConsentActive.asObservable();
    }
    public $cookieConsentData: BehaviorSubject<ICookieConsent> = new BehaviorSubject(this._initialCookieConsentData);
    public get cookieConsentData$(): Observable<ICookieConsent> {
        return this.$cookieConsentData.asObservable();
    }

    constructor(
        private _cookieService: CookieService,
        private _localStorage: StorageMap,
        private _configurationService: ConfigurationService,
        private _appInfoService: AppInfoService,
        private _siteSettingsService: SiteSettingsService,
        private _translationService: TranslationService,
        private _translateService: TranslateService,
        private _trackingV2Service: TrackingV2Service
    ) {
        if (this._configurationService.getSiteFromUrl()) {
            this._site = this._configurationService.getSiteFromUrl().site;
        }

        this._window = window;
        this.cookieConsentTranslations = this._translationService.getAllTranslations('dacookieconsent');

        this._translateService.get('dacookieconsent'.toLowerCase()).subscribe((data) => {
            this._$translations.next(data);
            this.cookieConsentTranslations = data;
        });

        this._subscription.push(
            this._configurationService.isSiteLoaded$.subscribe((siteLoaded) => {
                if (siteLoaded) {
                    this.cookieConsentTranslations = this._translationService.getAllTranslations('dacookieconsent');
                    this.cookieReplacmentTranslations = this._translationService.getAllTranslations('dacookiereplacement');
                    this.cookieConsentActive = this.cookieConsentFeatureActiveCheck();
                    if (this._configurationService.isPartnerPage()) {
                        this.cookieConsentFeatureActiveCheckInPortal();
                    }
                }
            })
        );
    }

    public cookieConsentFeatureActiveCheck(): boolean {
        return this._configurationService.hasFeature('ActiveApplications');
    }

    public cookieConsentFeatureActiveCheckInPortal() {
        this._configurationService.currentPortal$.subscribe((portalId) => {
            if (portalId) {
                this._$portalId.next(portalId);
                this.currentPortal = this._$portalId.getValue();
                this.cookieConsentTranslations = this._translationService.getAllTranslations('dacookieconsent');
                this.cookieReplacmentTranslations = this._translationService.getAllTranslations('dacookiereplacement');
                this._configurationService.getConfigPartnerByPortal().then((values) => {
                    if (values) {
                        const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                        let hasCheckPrerequest;
                        if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                            hasCheckPrerequest = tempFeatures[0].value.filter((setting) => setting.name === 'ActiveApplications');
                        }
                        if (hasCheckPrerequest && hasCheckPrerequest[0] && hasCheckPrerequest[0].enabled) {
                            this.cookieConsentActive = true;
                        } else {
                            this.cookieConsentActive = false;
                        }
                        this._$cookieConsentActive.next(this.cookieConsentActive);
                    }
                });
            }
        });
    }

    public setCookie(cookieData: string): void {
        if (this._configurationService.getSiteFromUrl()) {
            this.site = this._configurationService.getSiteFromUrl().site;
        }
        this.portal = this._configurationService.portalId;
        if (this.site) {
            this._cookieService.set(
                'dra_cookie_consent_allowed_' + this.site,
                cookieData,
                parseInt(this.cookieConsentTranslations.saveddays, 10),
                '/'
            );
        } else if (this.portal) {
            this._cookieService.set(
                'dra_cookie_consent_allowed_' + this.portal,
                cookieData,
                parseInt(this.cookieConsentTranslations.saveddays, 10),
                '/'
            );
        }
        this.updateCookieConsentData(cookieData);
        this._triggerEventForTracking();
    }

    public checkSiteCookieConsent(): void {
        if (this._configurationService.getSiteFromUrl()) {
            this.site = this._configurationService.getSiteFromUrl().site;
        }
        this.portal = this._configurationService.portalId;
        if (this.site) {
            this.siteCookieConsentExist = this._cookieService.check('dra_cookie_consent_allowed_' + this.site);
        } else if (this.portal) {
            this.siteCookieConsentExist = this._cookieService.check('dra_cookie_consent_allowed_' + this.portal);
        }
        if (!this.siteCookieConsentExist) {
            const _sub = this._trackingV2Service.trackingScriptLoaded$.subscribe((value) => {
                if (value) {
                    this.showCookieConsentOverlay$.next(true);
                    _sub.unsubscribe();
                }
            });
            this.showCookieConsentOverlay$.next(true);
            this._subscription.push(_sub);
        } else {
            const _cookieValue = decodeURIComponent(this.getSiteCookieConsent());
            this.updateCookieConsentData(_cookieValue);
            // this.showState2$.next(true);
        }
    }

    public getSiteCookieConsent(): string {
        if (this._configurationService.getSiteFromUrl()) {
            this.site = this._configurationService.getSiteFromUrl().site;
        }
        this.portal = this._configurationService.portalId;
        if (this.site) {
            return this._cookieService.get('dra_cookie_consent_allowed_' + this.site);
        } else if (this.portal) {
            return this._cookieService.get('dra_cookie_consent_allowed_' + this.portal);
        }
    }

    public updateCookieConsentData(cookieData: string): void {
        const _newCookieData = this.$cookieConsentData.getValue();
        // tslint:disable-next-line:forin
        for (const prop in this._initialCookieConsentData) {
            _newCookieData[prop] = parseInt(cookieData.split(ECookieConsentStringMappingEnum[prop])[1].charAt(0), 10);
            this._translateService.get('DaCookieConsent'.toLowerCase()).subscribe((data) => {
                this._$translations.next(data);
                this.cookieConsentTranslations = data;
                this.getCookiesToCheck = [];
                // tslint:disable-next-line:forin
                for (const item in this.cookieConsentTranslations) {
                }
                Object.entries(this.cookieConsentTranslations).forEach((key, value) => {
                    const keyOfCookieConsent = key.toString().split(',')[0];
                    const valueOfCookieConsent = key.toString().split(',');
                    valueOfCookieConsent.shift();

                    if (keyOfCookieConsent === prop + 'Cookies') {
                        this.getCookiesToCheck.push(valueOfCookieConsent.toString());
                    }
                });
            });

            if (_newCookieData[prop] === 0 && this.getCookiesToCheck) {
                const _removeCookies = this.getCookiesToCheck.toString().split(',');
                _removeCookies.forEach((cookie) => {
                    this._cookieService.delete(cookie, '/');
                    this._cookieService.delete(cookie);
                    localStorage.removeItem(cookie);
                    sessionStorage.removeItem(cookie);
                });
            }
        }
        this.$cookieConsentData.next(_newCookieData);
        this._checkGTMFromMarketing();
        if (parseInt(this.cookieConsentTranslations.version) > _newCookieData.Version) {
            this.showCookieConsentOverlay$.next(true);
        }
    }

    private _checkGTMFromMarketing() {
        const _cookieData = this.$cookieConsentData.getValue();
        if (_cookieData.Marketing === 1 && !!this.gtmContainerId && this._pageRendered) {
            this.createGTM();
        }
    }

    public acceptCookie(type: string) {
        const _currentCookieValue = this.getSiteCookieConsent();
        const _prefix = ECookieConsentStringMappingEnum[type];
        const _index = _currentCookieValue.indexOf(_prefix) + 3;
        const firstPart = _currentCookieValue.substr(0, _index);
        const lastPart = _currentCookieValue.substr(_index + 1);
        this.setCookie(firstPart + '1' + lastPart);
    }

    public openCookieSettingsOverlay() {
        this.showCookieConsentOverlay$.next(true);
    }

    public openCookieSettingsOverlayFromFooter() {
        this.showCookieConsentOverlayFromFooter$.next(true);
    }

    private _triggerEventForTracking(): void {
        const _currentCookieConsentData = this.$cookieConsentData.getValue();
        const _trackCookieSettingsData: TrackCookieSettingsInterface = {
            TechnicalRequired: 1,
            Functional: _currentCookieConsentData.Functional,
            Statistics: _currentCookieConsentData.Statistics,
            Marketing: _currentCookieConsentData.Marketing
        };
        const event = document.createEvent('CustomEvent');

        event.initCustomEvent('cookieConsentAllowed', true, true, _trackCookieSettingsData);
        document.dispatchEvent(event);
    }

    public getContainerIdOfGTM(): string {
        const _GoogleTagManager = this._siteSettingsService.getValue('GoogleTagManager');
        this.gtmContainerId = _GoogleTagManager ? _GoogleTagManager.ContainerId : null;
        return this.gtmContainerId;
    }

    // public checkToLoadGTMAfterPageRendered(): void {
    //     this._subscription.push(
    //         this._referenceService.pagerendered.subscribe((value) => {
    //             if (value === 'pagerendered') {
    //                 this._pageRendered = true;
    //                 if (!this.cookieConsentActive) {
    //                     this.createGTM();
    //                 } else {
    //                     this._checkGTMFromMarketing();
    //                 }
    //             }
    //         })
    //     );
    // }

    public createGTM() {
        if (!this._window['dataLayer']) {
            const _script = document.createElement('script');
            _script.type = 'text/javascript';
            _script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${this.gtmContainerId}');`;
            this._window.document.getElementsByTagName('head')[0].appendChild(_script);

            const _noscript = document.createElement('noscript');
            _noscript.innerHTML = `<iframe src="//www.googletagmanager.com/ns.html?id=${this.gtmContainerId}"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            this._window.document.getElementsByTagName('body')[0].appendChild(_noscript);
        }
    }
}
